<script setup>
// One card on a card level: a group at the all level ("Competition types ·
// 5 areas") or an area inside a group ("Box leagues · 8 parts"). Just a large
// illustration, the name and the count: no description, no preview of what is
// inside (Grant 2026-10-25: the overview showed too much at once; an area's
// line reads once you are inside it). The whole card is the target: hover
// lifts it, click flies into it. Nothing inside it can be reached from here.
// On a short canvas the count grows by --area-type-k (overviewTypeScale) so it
// stays readable at the fitted scale; the name grows more gently. The name
// block reserves two lines with the words at the bottom, so names sit on one
// baseline across a row whatever wraps. On a phone the card is a two-across
// tile with its own type and the illustration in the flow, beside the count.
import SystemMapArt from '~/components/systemMap/SystemMapArt.vue';
import {AREA_COUNT_PX, AREA_COUNT_PX_COMPACT, AREA_NAME_PX, AREA_NAME_PX_COMPACT} from '~/utils/systemMapLayout';

// Type sizes from the layout module, so the test can prove what a card comes to
// on screen (a count pill never under 11px, desktop or phone).
const typeVars = {'--area-name-px': `${AREA_NAME_PX}px`, '--area-count-px': `${AREA_COUNT_PX}px`};
const compactTypeVars = {'--area-name-px': `${AREA_NAME_PX_COMPACT}px`, '--area-count-px': `${AREA_COUNT_PX_COMPACT}px`};

const props = defineProps({
  area: {type: Object, required: true},      // {id, x, y, w, h, count} (a group's or an area's card)
  section: {type: Object, required: true},   // {name, art, admin?} the group or the area
  tone: {type: Object, required: true},      // {tint, ink}
  unit: {type: String, default: 'parts'},    // what the count counts: 'parts' (an area) or 'areas' (a group)
  interactive: {type: Boolean, default: true},
  compact: {type: Boolean, default: false},  // the phone's two-across tile: its own type
  current: {type: Boolean, default: false},  // the entered card, one level down
  dim: {type: Boolean, default: false},      // a search with no match inside
  hot: {type: Boolean, default: false},      // related to the hovered card
  matches: {type: Number, default: 0},       // search matches inside, 0 = none
});
const emit = defineEmits(['enter', 'hover']);

const countWord = computed(() => {
  const one = props.unit === 'areas' ? 'area' : 'part';
  return `${props.area.count} ${props.area.count === 1 ? one : `${one}s`}`;
});
</script>

<template>
  <div
      class="sma-card"
      :class="{'sma-card--dim': dim, 'sma-card--hot': hot, 'sma-card--current': current, 'sma-card--static': !interactive, 'sma-card--compact': compact}"
      :style="{left: `${area.x}px`, top: `${area.y}px`, width: `${area.w}px`, height: `${area.h}px`, '--area-tint': tone.tint, '--area-ink': tone.ink, ...(compact ? compactTypeVars : typeVars)}"
      :data-area="unit === 'parts' ? area.id : undefined"
      :data-group="unit === 'areas' ? area.id : undefined"
  >
    <button
        type="button"
        class="sma-btn"
        :tabindex="interactive ? 0 : -1"
        :aria-label="`${section.name}: ${countWord}. Open it.`"
        :aria-disabled="!interactive || undefined"
        @click="interactive && emit('enter', area.id)"
        @mouseenter="interactive && emit('hover', area.id)"
        @mouseleave="interactive && emit('hover', null)"
        @focus="interactive && emit('hover', area.id)"
        @blur="interactive && emit('hover', null)"
    >
      <span class="sma-top">
        <span class="sma-count">{{ countWord }}</span>
        <span v-if="section.admin" class="sma-tag">Admin feature</span>
        <span v-if="matches" class="sma-matches">{{ matches }} {{ matches === 1 ? 'match' : 'matches' }}</span>
        <span v-if="compact" class="sma-figure">
          <system-map-art :name="section.art" :size="72" class="sma-illu"/>
        </span>
      </span>
      <span v-if="!compact" class="sma-figure">
        <system-map-art :name="section.art" :size="130" class="sma-illu"/>
      </span>
      <span class="sma-name" lang="en">{{ section.name }}</span>
      <span class="sma-go" aria-hidden="true"><v-icon size="22">mdi-arrow-right</v-icon></span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.sma-card {
  position: absolute;
  transition: opacity 220ms ease;

  &--dim {
    opacity: 0.28;
  }
}

.sma-btn {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 26px 30px 28px;
  text-align: left;
  border: 1.5px solid transparent;
  border-radius: 32px;
  background:
      radial-gradient(120% 90% at 100% 0%, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0) 60%),
      var(--area-tint);
  color: rgb(var(--v-theme-text));
  font-family: inherit;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(26, 26, 26, 0.03), 0 18px 40px -24px rgba(26, 26, 26, 0.25);
  transition: transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 260ms ease, border-color 200ms ease;
  overflow: hidden;

  &:hover,
  &:focus-visible {
    transform: translateY(-8px);
    border-color: var(--area-ink);
    box-shadow: 0 2px 4px rgba(26, 26, 26, 0.03), 0 30px 60px -28px rgba(26, 26, 26, 0.35);
  }

  &:focus-visible {
    outline: 3px solid rgb(var(--v-theme-primary));
    outline-offset: 4px;
  }
}

.sma-card--static .sma-btn {
  cursor: default;
  pointer-events: none;

  &:hover {
    transform: none;
    border-color: transparent;
  }
}

/* Entering a card level: the card that happens to be under a still pointer does not light up until it moves (R53 L4) */
.sm--hover-off .sma-btn:hover {
  transform: none;
  border-color: transparent;
  box-shadow: 0 2px 4px rgba(26, 26, 26, 0.03), 0 18px 40px -24px rgba(26, 26, 26, 0.25);

  .sma-figure {
    transform: none;
  }

  .sma-go {
    opacity: 0;
    transform: translateX(6px);
  }
}

.sma-card--current .sma-btn {
  border-color: var(--area-ink);
}

.sma-card--hot .sma-btn {
  border-color: var(--area-ink);
  box-shadow: 0 0 0 6px color-mix(in srgb, var(--area-ink) 14%, transparent), 0 24px 50px -28px rgba(26, 26, 26, 0.35);
}

.sma-top {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 34px;
}

.sma-count,
.sma-tag,
.sma-matches {
  font-size: calc(var(--area-count-px, 19px) * var(--area-type-k, 1));
  font-weight: 800;
  letter-spacing: 0.2px;
  padding: 6px 14px;
  border-radius: 999px;
  line-height: 1.1;
  white-space: nowrap;
}

.sma-count {
  background: rgba(255, 255, 255, 0.92);
  color: var(--area-ink);
  border: 1px solid rgba(26, 26, 26, 0.06);
}

.sma-tag {
  color: rgb(var(--v-theme-charcoal));
  background: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: calc(15px * var(--area-type-k, 1));
}

.sma-matches {
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.sma-figure {
  position: absolute;
  right: 26px;
  top: 52px;
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.55);
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.sma-btn:hover .sma-figure {
  transform: scale(1.06) rotate(-2deg);
}

.sma-illu {
  color: var(--area-ink);

  :deep(.sma-a) {
    stroke: var(--area-ink);
    fill: rgba(255, 255, 255, 0.95);
  }

  :deep(.sma-f) {
    fill: var(--area-ink);
  }
}

.sma-name {
  margin-top: auto;
  font-size: calc(var(--area-name-px, 32px) * var(--area-name-k, 1));
  font-weight: 800;
  letter-spacing: -0.8px;
  line-height: 1.05;
  color: rgb(var(--v-theme-text));
  /* The name sits below the illustration, so it may run the card's width; the go arrow has its own corner */
  max-width: calc(100% - 60px);
  text-wrap: balance;
  hyphens: auto;
  /* Two lines reserved and the words at the bottom of them: one baseline across a row */
  min-height: 2.1em;
  display: flex;
  align-items: flex-end;
}

.sma-go {
  position: absolute;
  right: 26px;
  bottom: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: var(--area-ink);
  box-shadow: 0 6px 16px rgba(26, 26, 26, 0.08);
  transform: translateX(6px);
  opacity: 0;
  transition: opacity 200ms ease, transform 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.sma-btn:hover .sma-go,
.sma-btn:focus-visible .sma-go {
  opacity: 1;
  transform: translateX(0);
}

/* The phone's two-across tile: the illustration in the flow, in the top row
   beside the count (never behind the words), the name underneath at the tile's
   own (larger) type, no go arrow (no hover) */
.sma-card--compact {
  .sma-btn {
    padding: 20px 28px 26px;
    border-radius: 30px;
  }

  .sma-top {
    width: 100%;
    min-height: 72px;
    justify-content: space-between;
    align-items: flex-start;
  }

  .sma-figure {
    position: static;
    order: 2;
    margin-left: auto;
    width: 72px;
    height: 72px;
    flex: none;
  }

  .sma-name {
    max-width: 100%;
    margin-top: auto;
    letter-spacing: -1px;
    line-height: 1.05;
    min-height: 0;
    display: block;
  }

  .sma-go {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sma-btn, .sma-figure, .sma-go, .sma-card { transition: none; }
  .sma-btn:hover { transform: none; }
  .sma-btn:hover .sma-figure { transform: none; }
}
</style>
